import { useQuery } from '@tanstack/react-query';
import { Wallet, CircleNotch as Loader2 } from '@phosphor-icons/react';
import { fetchMyFeeNotes } from '@/lib/solicitorPortalApi';

export default function FeesPage() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['portal', 'feeNotes'],
    queryFn: fetchMyFeeNotes,
  });

  const totals = (data ?? []).reduce(
    (acc, f) => {
      acc.total += Number(f.total);
      acc.paid += Number(f.amountPaid);
      // MONEY-1: render the canonical server-computed outstanding (refund/credit/write-off aware) —
      // never recompute `total − paid` in the UI, which under-stated refunded/credited notes.
      acc.outstanding += Number((f as { outstanding?: number }).outstanding ?? 0);
      return acc;
    },
    { total: 0, paid: 0, outstanding: 0 },
  );

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Fee notes</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Invoices issued by chambers against your matters. Drafts (still in chambers) are not shown.
        </p>
      </div>

      {!isLoading && data && data.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-3">
          <Kpi label="Total billed" value={fmt(totals.total)} />
          <Kpi label="Paid" value={fmt(totals.paid)} tone="muted" />
          <Kpi label="Outstanding" value={fmt(totals.outstanding)} tone="warn" />
        </div>
      )}

      {isLoading && (
        <div className="flex items-center gap-2 rounded-lg border border-border bg-card p-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading fee notes…
        </div>
      )}

      {isError && (
        <div className="rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-5 text-sm text-red-800 dark:text-red-300">
          {error instanceof Error ? error.message : 'Could not load fee notes.'}
        </div>
      )}

      {data && data.length === 0 && (
        <div className="rounded-lg border border-dashed border-border bg-card p-10 text-center">
          <Wallet className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
          <div className="mb-1 text-sm font-medium text-foreground">No fee notes yet</div>
          <p className="text-xs text-muted-foreground">
            Issued fee notes for your matters will appear here.
          </p>
        </div>
      )}

      {data && data.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/30">
              <tr className="text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">Number</th>
                <th className="px-4 py-3">Matter</th>
                <th className="px-4 py-3">Issued</th>
                <th className="px-4 py-3">Due</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.map((f) => (
                <tr key={f.id} className="hover:bg-muted/30">
                  <td className="px-4 py-3 font-medium text-foreground">{f.feeNoteNumber}</td>
                  <td className="px-4 py-3">
                    <div className="text-foreground">{f.case.caseName}</div>
                    <div className="text-xs text-muted-foreground">{f.case.caseNumber}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {new Date(f.issueDate).toLocaleDateString('en-GB')}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {new Date(f.dueDate).toLocaleDateString('en-GB')}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-foreground">
                    {fmt(Number(f.total))}
                  </td>
                  <td className="px-4 py-3">
                    <StatusPill status={f.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="rounded-md border border-dashed border-border bg-muted/30 p-4 text-xs text-muted-foreground">
        <b>Coming next:</b> PDF download, payment integration, and aged-debtors flags.
      </div>
    </div>
  );
}

function Kpi({
  label,
  value,
  tone = 'brand',
}: {
  label: string;
  value: string;
  tone?: 'brand' | 'muted' | 'warn';
}) {
  const toneClass =
    tone === 'warn'
      ? 'text-amber-700 dark:text-amber-300'
      : tone === 'muted'
      ? 'text-muted-foreground'
      : 'text-foreground';
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className={`mt-1 text-xl font-semibold tracking-tight ${toneClass}`}>{value}</div>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const tone =
    status === 'paid'
      ? 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
      : status === 'overdue'
      ? 'bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300'
      : 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300';
  return (
    <span className={`rounded px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${tone}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

function fmt(n: number): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n);
}
